import React, { useEffect, useState, Suspense, Fragment } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet';

import * as THREE from 'three';
import { Canvas, useLoader, useThree } from "@react-three/fiber";
import { Box } from "@react-three/drei";

import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import { useTranslation } from "react-i18next";

import { useTheme } from '@mui/material';
import { Article } from '@mui/icons-material';

import apiClient from "../../../../services/api";
import store from "../../../../store";
import { toggleMessageOpen } from "../../../../store/actions/Message";

import Lights from "../../easyBoardOnline/partials/Lights";
import Background from "../../../partials/project/view/Background";
import Floor from "../../../partials/project/view/Floor";
import Cameras from "../../../partials/project/view/Cameras";
import Controls from "../../../partials/project/view/Controls";

import { setSideBarIsOpened } from "../../../../store/actions/Project";

import { calcCameraDistance } from "../../../../utils/Utils";
import CubesIcon from '../../../icons/CubesIcon';
import CubeIcon from '../../../icons/CubeIcon';

import { setSelectedElement, setSelectedProject } from '../../../../store/actions/ScannedProject';
import { useSelector } from "react-redux";
import StyledIconButton from "../../../partials/easyFloor/StyledIconButton";

const SHADOWS = false;
const CAMERA_DISTANCE = 100;

const Objects = React.memo(({project}) => {
    return (
        <>
            <Pile project={project} />,
            <BackPanel project={project} />,
            <ShelvesWithProducts project={project} />
            <PegsWithProducts project={project} />
        </>
    );
});

const Pile = React.memo(({project}) => {
    let positions = [];

    project.products.forEach(segmentData => {
        const segment = segmentData.segment - 1;

        positions.push([segment*Number(project.shelf_width), 0, 0]);
        positions.push([segment*Number(project.shelf_width) + Number(project.shelf_width), 0, 0]);
    });

    const element = {
        'data': project.pile,
        'object': project.pile.gltf,
        'positions': positions
    };

    return <ObjectGroup key={`g${project.pile.object_id}`} element={element} item={project.pile} isFixture={true} />
});

const BackPanel = React.memo(({project}) => {
    let positions = [];

    project.products.forEach(segmentData => {
        const segment = segmentData.segment - 1;

        const nrOfBackPanels = Math.floor(project.pile.height / project.backPanel.height);

        for(let j = 0; j < nrOfBackPanels; j++) {
            positions.push([segment*Number(project.shelf_width) + Number(project.pile.width / 2), j*project.backPanel.height, 0]);
        }
    });

    const element = {
        'data': project.backPanel,
        'object': project.backPanel.gltf,
        'positions': positions
    }

    return <ObjectGroup key={`g${project.backPanel.object_id}`} element={element} item={project.backPanel} isFixture={true} />
});

const ShelvesWithProducts = ({project}) => {
    if(!project.products_placeable) {
        return;
    }

    const segments = project.products_placeable;

    const shelfPositions = [];
    const productPositions = [];

    let segmentOffsetX = 0;

    segments.forEach((segmentData) => {
        const segment = segmentData.segment;
        const rows = segmentData.rows;
        
        let segmentOffsetY = 0;
        let segmentOffsetX = ((segment - 1) * project.backPanel.width) + Number(project.pile.width) / 2;

        rows.sort( function ( a, b ) { return b.row - a.row; } );

        rows.forEach((rowData, rowIndex) => {
            const row = rowData.row;
            const products = rowData.products;

            const element = {
                segment: segment,
                row: row,
                position: [
                    segmentOffsetX,
                    segmentOffsetY,
                    (segmentOffsetY != 0 ? project.shelf.depth : project.bottomShelf.depth) - 2
                ]
            };

            shelfPositions.push(element);

            let max = 0;
            let productOffsetX = ((segment - 1) * project.backPanel.width) + Number(project.pile.width);

            products.forEach(productData => {
                let productOffsetZ = 0;
                const product = productData.product;
                const height = product.object ? Number(product.object.height) : 10;
                const width = product.object ? Number(product.object.width) : 10;
                const depth = product.object ? Number(product.object.depth) : 10;

                if(height > max) {
                    max = height + 5;
                }

                let element = { product: product, positions: [] };
                const positions = [];
                let position = [];

                for(let i = 0; i < productData.product.quantity; i++) {
                    if(product.object) {
                        position = [
                            productOffsetX - (width / 2), 
                            segmentOffsetY - (height / 2) + Number(project.shelf.height), 
                            (segmentOffsetY != 0 ? Number(project.shelf.depth) : project.bottomShelf.depth ) - (depth / 2) - productOffsetZ - 5
                        ];
                    } else {
                        position = [
                            productOffsetX + (width / 2), 
                            segmentOffsetY + (height / 2) + Number(project.shelf.height), 
                            (segmentOffsetY != 0 ? Number(project.shelf.depth) : project.bottomShelf.depth ) - (depth / 2) - productOffsetZ - 5
                        ];
                    }

                    positions.push(position);
                    productOffsetZ += depth + 0.5;
                }

                element.positions = positions;

                productPositions.push(element);
                productOffsetX += width;
            });

            segmentOffsetY += max;
        })
        
        segmentOffsetX += Number(project.shelf_width);
    });

    let objects = [];

    productPositions.forEach(item => {
        if(!item?.product?.object || !item?.product?.object?.gltf) {
            return;
        }

        const exists = objects.filter((obj) => {
            return obj?.object?.id == item?.product?.object?.id
        });

        if(!exists || exists.length == 0) {
            objects.push({
                object: item.product.object,
                positions: item.positions,
                product: item.product
            })
        } else {
            exists[0].positions.push(item.positions[0]);
        }
    });

    return (
        <>
            <Shelves data={shelfPositions} object={project.shelf} bottomShelfObject={project.bottomShelf} />
            <ProductsWithoutObj data={productPositions} />
            <ProductsWithObj objects={objects} hangable={false} />
        </>
    );
}

const PegsWithProducts = ({project}) => {
    const segments = project.products_hangable;
    
    const pegPositions = [];
    const productPositions = [];

    let segmentOffsetX = 0;

    segments.forEach((segmentData) => {
        const segment = segmentData.segment;
        const rows = segmentData.rows;

        const nrOfBackPanels = Math.floor(project.pile.height / project.backPanel.height);
        let segmentOffsetY = (nrOfBackPanels * project.backPanel.height) - project.peg.height;

        rows.forEach((rowData, rowIndex) => {
            const row = rowData.row;
            const products = rowData.products;

            let max = 0;
            let productOffsetX = ((segment - 1) * project.backPanel.width) + Number(project.pile.width);

            products.forEach(productData => {
                let productOffsetZ = 0;
                const product = productData.product;
                const height = product.object ? Number(product.object.height) : 10;
                const width = product.object ? Number(product.object.width) : 10;
                const depth = product.object ? Number(product.object.depth) : 10;

                let position = [];

                if(product.object) {
                    position = [
                        productOffsetX + Number(product?.object?.dock_point_x) - (Number(project.peg.width) / 2),
                        segmentOffsetY - (height - Number(product?.object?.dock_point_y)) - (Number(project.peg.height) / 2),
                        project.peg.depth
                    ];
                } else {
                    position = [
                        productOffsetX + (width / 2) - (Number(project.pile.width) / 2),
                        segmentOffsetY,
                        project.peg.depth
                    ];
                }

                const pegElement = {
                    segment: segment,
                    row: row,
                    position: position
                };
                
                pegPositions.push(pegElement);

                if(height > max) {
                    max = height + 5;
                }

                let element = { product: product, positions: [] };
                const positions = [];

                for(let i = 0; i < productData.product.quantity; i++) {
                    let position = [
                        productOffsetX + (width / 2),
                        segmentOffsetY - (height / 2) + (!product.object ? Number(project.shelf.height) : 0),
                        project.peg.depth - productOffsetZ - 2
                    ];

                    positions.push(position);
                    productOffsetZ += depth + 0.5;
                }

                element.positions = positions;

                productPositions.push(element);
                productOffsetX += width;
            });

            segmentOffsetY -= max;
        })
        
        segmentOffsetX += Number(project.shelf_width);
    });

    let objects = [];

    productPositions.forEach(item => {
        if(!item?.product?.object || !item?.product?.object?.gltf) {
            return;
        }

        const exists = objects.filter((obj) => {
            return obj?.object?.id == item?.product?.object?.id
        });

        if(!exists || exists.length == 0) {
            objects.push({
                object: item.product.object,
                positions: item.positions,
                product: item.product
            })
        } else {
            exists[0].positions.push(item.positions[0]);
        }
    });

    return (
        <>
            <Pegs data={pegPositions} object={project.peg} />
            <ProductsWithoutObj data={productPositions} />
            <ProductsWithObj objects={objects} hangable={true} />
        </>
    );
}

const Shelves = ({data, object, bottomShelfObject }) => {
    if(!data) {
        return;
    }

    return (
        data.map((shelf, index) => {
            return <Object index={index} element={shelf} object={object} bottomShelfObject={bottomShelfObject} isFixture={true} />
        })
    )
}

const Pegs = ({data, object}) => {
    if(!data) {
        return;
    }

    return (
        data.map((shelf, index) => {
            return <Object index={index} element={shelf} object={object} isPeg={true} isFixture={true} />
        })
    )
}

const ProductsWithoutObj = ({data}) => {
    if(!data) {
        return;
    }

    return (
        data.map((product, index) => {
            return (
                product.positions.map((position, positionIndex) => {
                    if(product?.product?.object || product?.product?.object?.gltf) {
                        return;
                    }

                    return <Product data={product} pos={position} />;
                })
            );
        })
    )
}

const ProductsWithObj = ({objects, hangable}) => {
    return (
        objects.map((item) => (
            item.positions.map(position => (
                <ProductObject object={item.object} position={position} hangable={hangable} product={item.product} />
            ))
        ))
    )
}

const ProductObject = React.memo(({object, position, hangable, product}) => {
    const gltf = useLoader(GLTFLoader, object.gltf);
    const copiedScene = React.useMemo(() => gltf.scene.clone(), [gltf.scene]);
    const selectedElement = useSelector((state) => state.scannedProject.selectedElement);
    const gl = useThree();

    const [originalColors, setOriginalColors] = useState([]);

    let updatedPosition = position;

    const A = Number(object.dock_point_angle_x) != 0 ? Number(Number(object.height) * Math.sin(degreesToRadians(object.dock_point_angle_x))) : 0;
    const B = Number(object.dock_point_angle_y) != 0 ? Number(Number(object.width) * Math.sin(degreesToRadians(object.dock_point_angle_y))) : 0;
    const C = Number(object.dock_point_angle_z) != 0 ? Number(Number(object.height) * Math.sin(degreesToRadians(object.dock_point_angle_z))) : 0;

    if(!hangable) {
        updatedPosition = [
            position[0] + Number(object.width / 2),
            position[1] + Number(object.height / 2),
            position[2] + Number(object.depth / 2)
        ];
    } else {
        updatedPosition = [
            position[0] - Number(object.width / 2),
            position[1] - Number(object.height / 2),
            position[2]
        ];
    }

    if(Number(object.dock_point_angle_z) != 0) {
        updatedPosition = [
            -(position[0] - Number(object.width / 2)),
            -(position[1] - Number(object.height / 2)),
            -position[2]
        ];
    }
        
    useEffect(() => {
        if(!copiedScene) {
            return;
        }

        let colors = [];

        copiedScene.children.forEach((children) => {
            children.traverse((object) => {
                if(object.isMesh) {
                    const color = new THREE.Color(
                        object.material.color.r,
                        object.material.color.g,
                        object.material.color.b,
                    );

                    colors[object.uuid] = color;
                }
            });
        });

        setOriginalColors(colors);

        copiedScene.traverse((object) => {
            if(object.isMesh === true && object.material.map !== null) {
                object.material.map.anisotropy = gl?.gl.capabilities.getMaxAnisotropy();
            }
        });
    }, [copiedScene]);

    useEffect(() => {
        copiedScene.children.forEach((children) => {
            children.traverse((object) => {
                if(object.isMesh === true) {
                    
                    object.material.color.set(originalColors[object.uuid]);

                    if(product?.id == selectedElement?.id) {
                        object.material.color.set('#00FF00');
                    }
                }
            });
        })
    }, [selectedElement])

    return (
        <primitive
            object={copiedScene}
            position={updatedPosition}
            rotation={[
                degreesToRadians(Number(object.dock_point_angle_x)),
                degreesToRadians(Number(object.dock_point_angle_y)),
                degreesToRadians(Number(object.dock_point_angle_z))
            ]}
            onClick={(event) => {
                event.stopPropagation();
                store.dispatch(setSelectedElement((!selectedElement || selectedElement.id != product.id) ? product : null));
            }}
        />
    );
});

const degreesToRadians = (degrees) => degrees * (Math.PI / 180);

const Object = (props) => {
    const { element, object, bottomShelfObject, index, isPeg, isFixture } = props;

    let model = element.position[1] != 0 ? object.gltf : bottomShelfObject?.gltf;

    if(isPeg) {
        model = object.gltf;
    }

    const gltf = useLoader(GLTFLoader, model);

    return (
        <group dispose={null} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            <Primitive
                index={index}
                scene={gltf.scene}
                position={element.position}
                element={isPeg ? object : (element.position[1] != 0) ? object : bottomShelfObject}
                isFixture={isFixture}
            />
        </group>
    );
}

const hoveredColor = 0x90EE90;
const selectedColor = 0x00FF00;

const materialLambertWhite = <meshLambertMaterial attach="material" color="lightgrey" depthWrite="false" />;
const materialLambertWhite0 = <meshLambertMaterial key="0" attach="material-0" color="lightgrey" depthWrite="false" />;
const materialLambertWhite1 = <meshLambertMaterial key="1" attach="material-1" color="lightgrey" depthWrite="false" />;
const materialLambertWhite2 = <meshLambertMaterial key="2" attach="material-2" color="lightgrey" depthWrite="false" />;
const materialLambertWhite3 = <meshLambertMaterial key="3" attach="material-3" color="lightgrey" depthWrite="false" />;
const materialLambertWhite5 = <meshLambertMaterial key="5" attach="material-5" color="lightgrey" depthWrite="false" />;

const selectedMaterialLambert = <meshLambertMaterial attach="material" color={selectedColor} depthWrite="false" />;
const selectedMaterialLambert0 = <meshLambertMaterial key="0" attach="material-0" color={selectedColor} depthWrite="false" />;
const selectedMaterialLambert1 = <meshLambertMaterial key="1" attach="material-1" color={selectedColor} depthWrite="false" />;
const selectedMaterialLambert2 = <meshLambertMaterial key="2" attach="material-2" color={selectedColor} depthWrite="false" />;
const selectedMaterialLambert3 = <meshLambertMaterial key="3" attach="material-3" color={selectedColor} depthWrite="false" />;
const selectedMaterialLambert5 = <meshLambertMaterial key="5" attach="material-5" color={selectedColor} depthWrite="false" />;

const Product = ({data, pos}) => {
    const object = data.product ? data.product.object : null;
    const size = object ? [object.width, object.height, object.depth] : [10, 10, 10];

    if((object && (pos[2] - object.depth < 0)) || (!object && Number(pos[2]) - 10 < 0 )) {
        return null;
    }

    if(object && object.image) {
        return (
            <Suspense fallback={<ProductBox pos={pos} size={size} product={data.product} />}>
                <ProductWithImage image={object.image} size={size} position={pos} product={data.product} />
            </Suspense>
        );
    }

    return <ProductBox pos={pos} size={size} product={data.product} />;
}

const ProductBox = (props) => {
    const { pos, size, product } = props;
    const selectedElement = useSelector((state) => state.scannedProject.selectedElement);

    const [material, setMaterial] = useState(materialLambertWhite);

    useEffect(() => {
        if(!selectedElement || selectedElement.id != product.id) {
            setMaterial(materialLambertWhite);

            return;
        }

        setMaterial(selectedMaterialLambert);
    }, [selectedElement]);

    return (
        <Box
            position={pos}
            args={size}
            castShadow={SHADOWS}
            receiveShadow={SHADOWS}
            onClick={(event) => {
                event.stopPropagation();
                store.dispatch(setSelectedElement((!selectedElement || selectedElement.id != product.id) ? product : null));
            }}
        >
            {material}
        </Box>
    );
};

const ProductWithImage = ({ image, size, position, product }) => {
    const { gl } = useThree();
    const selectedElement = useSelector((state) => state.scannedProject.selectedElement);

    const [materials, setMaterials] = useState(materialLambertWhite);

    const map = useLoader(THREE.TextureLoader, image);

    useEffect(() => {
        gl.initTexture(map);
        setMaterials([
            materialLambertWhite0, materialLambertWhite1,
            materialLambertWhite2, materialLambertWhite3,
            <meshLambertMaterial key="4" attach="material-4" map={map} depthWrite="false"/>,
            materialLambertWhite5,
        ]);
    }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(!selectedElement || selectedElement.id != product.id) {
            setMaterials([
                materialLambertWhite0, materialLambertWhite1,
                materialLambertWhite2, materialLambertWhite3,
                <meshLambertMaterial key="4" attach="material-4" map={map} depthWrite="false"/>,
                materialLambertWhite5,
            ]);

            return;
        }

        setMaterials([
            selectedMaterialLambert0, selectedMaterialLambert1,
            selectedMaterialLambert2, selectedMaterialLambert3,
            <meshLambertMaterial key="4" attach="material-4" map={map} depthWrite="false" color={selectedColor} />,
            selectedMaterialLambert5,
        ]);
    }, [selectedElement]);

    return (
        <mesh
            position={position}
            castShadow={SHADOWS}
            receiveShadow={SHADOWS}
            frustumCulled={false}
            onClick={(event) => {
                event.stopPropagation();
                store.dispatch(setSelectedElement((!selectedElement || selectedElement.id != product.id) ? product : null));
            }}
        >
            <boxGeometry args={size} />
            {materials}
        </mesh> 
    );
}

const ObjectGroup = ({ element, item, isFixture }) => {
    const { object, positions } = element;
    const gltf = useLoader(GLTFLoader, object);

    return (
        <group
            dispose={null}
            castShadow={SHADOWS}
            receiveShadow={SHADOWS}
        >
            {positions.map((position, index) => {
                return <Primitive
                            index={index}
                            scene={gltf.scene}
                            position={position}
                            element={element}
                            isFixture={isFixture}
                        />;
            })}
        </group>
    );
};

const Primitive = React.memo((props) => {
    const { scene, position, index, element, isFixture } = props;
    const copiedScene = React.useMemo(() => scene.clone(), [scene]);

    const selectedElement = useSelector((state) => state.scannedProject.selectedElement);

    React.useEffect(() => {
        if (selectedElement && selectedElement.index === index && selectedElement.id === element?.data?.id) {
            copiedScene.traverse((child) => {
                if (child.isMesh) {
                    // Check if this mesh corresponds to the selected element
                    if (child.name === element?.data?.meshName) { // Assuming you have a unique name or identifier for each mesh
                        child.material.color.setHex(selectedColor);
                    }
                }
            });
        }
    }, [selectedElement, copiedScene, index, element]);

    return (
        <primitive
            object={copiedScene}
            position={position}
            onClick={(event) => {
                event.stopPropagation();
                store.dispatch(setSelectedElement({ id: element?.data?.id, index: index, element: element, isFixture: isFixture }));
            }}
        />
    );
});

function getElementPosition(position)
{
    return position;
}

const Scene = React.memo((props) => {
    const { project, theme, is3D, distance, center } = props;

    const sceneIsLoaded = !!project;
    
    return (
        <>
            <Background color={theme.palette.background.default} />
            <Lights shadows={SHADOWS} />
            <Cameras perspective={is3D} position={{ ...center, z: distance }} />
            {project && <Controls rotate={is3D} project={project} target={{ ...center, z: 0 }}/>}
            {is3D && !!project && <Floor color={theme.palette.border.grid} />}
            {sceneIsLoaded && <Suspense fallback={null}><Objects project={project} key='objects' /></Suspense>}
        </>
    );
})

function ScannedProject()
{
    const theme = useTheme();
    const { t } = useTranslation();
    const { id } = useParams();
    
    const [center, setCenter] = useState({ x: 0, y: 0, z: 0 });
    const [distance, setDistance] = useState(CAMERA_DISTANCE);
    const [viewType, setViewType] = useState('3D');

    const [project, setProject] = useState(null);

    const projectLoaded = !!project;
    const is3D = viewType === '3D';

    useEffect(() => {
        apiClient.post('/api/scanned-projects/load', { id: Number(id) }).then((response) => {
            if (!response.data.success) {
                return;
            }

            setProject(response.data.project);
            store.dispatch(setSelectedProject(response.data.project));
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t('An error occurred while selecting project'), "error"))
        })
    }, []);

    useEffect(() => {
        if(project) {
            setCenter({
                x: (project.width) / 2, 
                y: (project.height) / 2, 
                z: 0
            });
        }
    }, [projectLoaded, project]);

    useEffect(() => {
        const distance = calcCameraDistance(center, true, 100);

        setDistance(distance);
    }, [center])
    
    const style = {
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        backgroundColor: theme.palette.background.primary
    };

    return (
        <div style={style}>
            <Canvas shadows={SHADOWS} gl={{preserveDrawingBuffer: true}}>
                {projectLoaded && 
                    <Scene 
                        theme={theme}
                        is3D={is3D}
                        center={center}
                        distance={distance}
                        project={project}
                    />
                }
            </Canvas>
            <FloatingButtons
                viewType={viewType}
                setViewType={setViewType}
            />
        </div>
    );
}

const FloatingButtons = ({ viewType, setViewType }) => {
    const { t } = useTranslation();

    const sideBarIsOpened = useSelector((state) => state.project.sideBarIsOpened);

    const style = {
        position: 'absolute',
        top: '50%',
        right: '0px',
        transform: 'translateX(-50%)',
        zIndex: 9999
    };

    return (
        <Fragment>
            <Helmet>
                <title>easyBoardOnline</title>
            </Helmet>
            <div style={style}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px'}}>
                        <StyledIconButton
                            color="primary"
                            title={t('Change View')}
                            isActive={false}
                            onClick={() => {
                                if(viewType == '3D') {
                                    setViewType('2D');
                                } else {
                                    setViewType('3D');
                                }
                            }}
                        >
                            {viewType != '3D' ? <CubeIcon /> : <CubesIcon />}
                        </StyledIconButton>
                        <StyledIconButton
                            color="primary"
                            title={t('Show report')}
                            isActive={sideBarIsOpened}
                            onClick={() => {
                                store.dispatch(setSideBarIsOpened(sideBarIsOpened ? false : true));
                            }}
                        >
                            <Article />
                        </StyledIconButton>
                </div>
            </div>
        </Fragment>
    )
};

export default ScannedProject;
